/**
 * Types for the Visual IDF Object Editor
 */

import type { IDFObject, IDFField } from '../parser/types';
import type { IDDField } from '../idd/types';

/**
 * Validation status for objects and fields
 */
export type ValidationStatus = 'valid' | 'warning' | 'error';

/**
 * Validation error with details
 */
export interface ValidationError {
  type: 'required' | 'type' | 'reference' | 'range' | 'unique' | 'node' | 'choice';
  message: string;
  suggestion?: string;
}

/**
 * Validation warning with details
 */
export interface ValidationWarning {
  type: 'out-of-range' | 'unusual' | 'deprecated' | 'orphan' | 'empty-reference';
  message: string;
  suggestion?: string;
}

/**
 * Field-level validation result
 */
export interface FieldValidation {
  status: ValidationStatus;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

/**
 * Object-level validation result
 */
export interface ObjectValidation {
  status: ValidationStatus;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  fields: FieldValidation[];
}

/**
 * Filter options for the object browser
 */
export interface ObjectFilter {
  searchQuery: string;
  showOnlyInvalid: boolean;
  showOnlyWarnings: boolean;
  groupFilter: string | null;
  classFilter: string | null;
}

/**
 * View mode for the editor
 */
export type ViewMode = 'cards' | 'table';

/**
 * Tree node for the object browser
 */
export interface ObjectTreeNode {
  id: string;
  type: 'group' | 'class' | 'object';
  name: string;
  validationStatus: ValidationStatus;
  children?: ObjectTreeNode[];
  objectCount?: number;
  objectClass?: string;
  objectData?: IDFObject;
}

/**
 * Field editor type based on IDD metadata
 */
export type FieldEditorType =
  | 'text'
  | 'number'
  | 'choice'
  | 'reference'
  | 'node'
  | 'autocalculatable'
  | 'autosizable';

/**
 * Props for field editor components
 */
export interface FieldEditorProps {
  object: IDFObject;
  field: IDFField | undefined;
  fieldInfo: IDDField;
  fieldIndex: number;
  validation: FieldValidation;
  referencedObjects: IDFObject[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Object reference information
 */
export interface ObjectReference {
  objectId: string;
  objectName: string;
  objectClass: string;
  fieldIndex: number;
  fieldName: string;
}

/**
 * Node connection information
 */
export interface NodeConnection {
  objectId: string;
  objectName: string;
  objectClass: string;
  fieldName: string;
  nodeType: 'inlet' | 'outlet' | 'unknown';
}

/**
 * Quick action types
 */
export type QuickAction =
  | { type: 'duplicate'; objectIds: string[] }
  | { type: 'delete'; objectIds: string[] }
  | { type: 'rename'; objectId: string; newName: string };

/**
 * Wizard step for creating new objects
 */
export type WizardStep = 'class' | 'required' | 'optional' | 'review';

/**
 * Field group for organized display
 */
export interface FieldGroup {
  name: string;
  startIndex: number;
  endIndex: number;
  isExtensible: boolean;
}

/**
 * Get the appropriate field editor type based on IDD field metadata
 */
export function getFieldEditorType(fieldInfo: IDDField): FieldEditorType {
  if (fieldInfo.autocalculatable) return 'autocalculatable';
  if (fieldInfo.autosizable) return 'autosizable';
  if (fieldInfo.choices && fieldInfo.choices.length > 0) return 'choice';
  if (fieldInfo.objectList) return 'reference';
  if (fieldInfo.type === 'node') return 'node';
  if (fieldInfo.type === 'real' || fieldInfo.type === 'integer') return 'number';
  return 'text';
}

/**
 * Get group icon based on group name
 */
export function getGroupIcon(groupName: string): string {
  const iconMap: Record<string, string> = {
    'Simulation Parameters': 'settings',
    'Location and Climate': 'location',
    'Schedules': 'calendar',
    'Surface Construction Elements': 'layers',
    'Thermal Zones and Surfaces': 'building',
    'Internal Gains': 'flash',
    'Zone Airflow': 'air',
    'HVAC Templates': 'template',
    'HVAC Design Objects': 'blueprint',
    'Zone HVAC Controls and Thermostats': 'thermostat',
    'Zone HVAC Air Loop Terminal Units': 'terminal',
    'Zone HVAC Equipment Connections': 'connect',
    'Fans': 'fan',
    'Coils': 'coil',
    'Pumps': 'pump',
    'Plant Heating and Cooling Equipment': 'equipment',
    'Plant-Condenser Loops': 'loop',
    'Air Distribution': 'airflow',
    'Node Branch Management': 'branch',
    'Output Reporting': 'report',
    'Economics': 'money',
    'Performance Curves': 'chart',
    'Fluid Properties': 'droplet',
    'Water Systems': 'water',
    'Electric Load Center-Generator Specifications': 'power',
    'Daylighting': 'sun',
    'Zone HVAC Radiative/Convective Units': 'radiator',
    'Zone HVAC Forced Air Units': 'hvac',
  };
  return iconMap[groupName] || 'folder';
}

/**
 * Get group display order for sorting
 */
export function getGroupOrder(groupName: string): number {
  const orderMap: Record<string, number> = {
    'Simulation Parameters': 1,
    'Location and Climate': 2,
    'Schedules': 3,
    'Surface Construction Elements': 4,
    'Thermal Zones and Surfaces': 5,
    'Internal Gains': 6,
    'Daylighting': 7,
    'Zone Airflow': 8,
    'Zone HVAC Controls and Thermostats': 9,
    'Zone HVAC Air Loop Terminal Units': 10,
    'Zone HVAC Equipment Connections': 11,
    'Zone HVAC Forced Air Units': 12,
    'Zone HVAC Radiative/Convective Units': 13,
    'Fans': 20,
    'Coils': 21,
    'Pumps': 22,
    'Air Distribution': 23,
    'Node Branch Management': 24,
    'Plant Heating and Cooling Equipment': 30,
    'Plant-Condenser Loops': 31,
    'HVAC Templates': 40,
    'HVAC Design Objects': 41,
    'Performance Curves': 50,
    'Fluid Properties': 51,
    'Water Systems': 52,
    'Output Reporting': 90,
    'Economics': 91,
  };
  return orderMap[groupName] || 100;
}
